import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ImageMockup } from "@/components/ImageMockup";
import api, { fileUrl, thumbUrl } from "@/lib/api";
import { colorSwatch } from "@/lib/colors";
import { readGarmentPick } from "@/pages/Merchandise";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Minus, Plus, Check, ArrowLeft, Image as ImageIcon, Upload, Eraser } from "lucide-react";
import { BgToggle, useBgPref } from "@/components/BgToggle";
import { GarmentDiagram } from "@/components/GarmentDiagram";
import { ShareDesign } from "@/components/ShareDesign";
import { GuidelinesLink } from "@/components/GuidelinesLink";
import { rememberDesign } from "@/components/RecentlyViewed";

function StockBadge({ stock }) {
  if (stock == null) return null;
  let cls = "text-[#10b981] border-[#10b981]/40 bg-[#10b981]/10", txt = `In stock · ${stock} units`;
  if (stock === 0) { cls = "text-red-400 border-red-500/40 bg-red-500/10"; txt = "Out of stock"; }
  else if (stock <= 5) { cls = "text-[#ffd600] border-[#ffd600]/40 bg-[#ffd600]/10"; txt = `Only ${stock} left`; }
  return <span data-testid="stock-badge" className={`inline-flex items-center gap-1.5 text-xs font-mono-p px-2.5 py-1 rounded-full border ${cls}`}><span className="w-1.5 h-1.5 rounded-full bg-current" />{txt}</span>;
}

export default function DesignDetail({ vendor = false }) {
  const { designId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const home = vendor ? "/vendor" : "/catalog";

  const { data: design, isLoading, isError } = useQuery({
    queryKey: ["design", designId],
    queryFn: async () => (await api.get(`/designs/${designId}`)).data,
    retry: false,
  });
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  useEffect(() => { if (design?.design_id) rememberDesign(design); }, [design]);
  const { data: placementOpts = [] } = useQuery({ queryKey: ["placements"], queryFn: async () => (await api.get("/placements")).data });
  const { data: allDesigns = [] } = useQuery({ queryKey: vendor ? ["vendor-designs"] : ["catalogdesigns"], queryFn: async () => (await api.get(vendor ? "/vendor/designs" : "/designs")).data });
  const { data: sizePricing = [] } = useQuery({ queryKey: ["sizepricing"], queryFn: async () => (await api.get("/size-pricing")).data });
  const { data: liveInvRes } = useQuery({ queryKey: ["vendor-stock"], queryFn: async () => (await api.get("/vendor/stock")).data, enabled: vendor, refetchInterval: vendor ? 20000 : false });
  const liveInv = liveInvRes?.stock;
  const stockOf = useCallback((pid, c, s) => (liveInv?.[pid]?.[c]?.[s] ?? 0), [liveInv]);
  const colorHasStock = useCallback((p, c) => !vendor || !liveInv || Object.values(liveInv?.[p.product_id]?.[c] || {}).some((n) => n > 0), [vendor, liveInv]);

  const [product, setProduct] = useState(null);
  const [bg, setBg] = useBgPref();
  const [color, setColor] = useState(null);
  const { data: mockup } = useQuery({ queryKey: ["mockup", product?.product_id, color], queryFn: async () => (await api.get("/mockups/resolve", { params: { product_id: product.product_id, color } })).data || null, enabled: !!product, staleTime: 60000 });
  const [size, setSize] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [placementArt, setPlacementArt] = useState({});
  const [artPickerFor, setArtPickerFor] = useState(null);
  const [artSearch, setArtSearch] = useState("");
  const [uploadingArt, setUploadingArt] = useState(false);
  const [bgBusyPl, setBgBusyPl] = useState(null);
  const [mockView, setMockView] = useState("both");
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (products.length && !product) {
      const g = readGarmentPick();
      const gp = g && products.find((p) => p.product_id === g.product_id);
      const p0 = gp || products[0];
      const vis0 = (p0.colors || []).filter((c) => !(p0.hidden_colors || []).includes(c));
      setProduct(p0);
      setColor(gp && g.color && p0.colors.includes(g.color) ? g.color : (vis0[0] || p0.colors[0]));
      setSize(gp && g.size && p0.sizes.includes(g.size) ? g.size : p0.sizes[0]);
      if (gp) toast.success(`Using your ${p0.name}${g.color ? ` · ${g.color}` : ""}${g.size ? ` · ${g.size}` : ""} from Merchandise`);
    }
  }, [products, product]);

  // Vendor mode: if the chosen colour/size runs out, hop to the first one still in stock.
  useEffect(() => {
    if (!vendor || !liveInv || !product) return;
    const vis = (product.colors || []).filter((c) => !(product.hidden_colors || []).includes(c) && colorHasStock(product, c));
    if (vis.length && !vis.includes(color)) { setColor(vis[0]); return; }
    if (color && stockOf(product.product_id, color, size) <= 0) {
      const ok = product.sizes.find((s) => stockOf(product.product_id, color, s) > 0);
      if (ok) setSize(ok);
    }
  }, [vendor, liveInv, product, color, size, colorHasStock, stockOf]);

  useEffect(() => { if (placementOpts.length && placements.length === 0) setPlacements([placementOpts[0].name]); }, [placementOpts, placements.length]);

  useEffect(() => {
    if (!design) return;
    setPlacementArt((prev) => {
      const next = {};
      placements.forEach((p) => { next[p] = prev[p] || { design_id: design.design_id, title: design.title, image_url: design.image_url }; });
      return next;
    });
  }, [placements, design]);

  const assignArt = (d) => {
    if (!artPickerFor) return;
    setPlacementArt((prev) => ({ ...prev, [artPickerFor]: { design_id: d.design_id, title: d.title, image_url: d.image_url } }));
    setArtPickerFor(null);
    setArtSearch("");
  };

  const uploadArt = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !artPickerFor) return;
    setUploadingArt(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setPlacementArt((prev) => ({ ...prev, [artPickerFor]: { design_id: null, title: f.name, image_url: data.url } }));
      toast.success("Artwork uploaded");
      setArtPickerFor(null); setArtSearch("");
    } catch { toast.error("Upload failed (PNG/JPEG only)"); }
    finally { setUploadingArt(false); }
  };

  const removeArtBg = async (p) => {
    const art = placementArt[p];
    if (!art?.image_url) return;
    setBgBusyPl(p);
    const tid = toast.loading("Removing background…");
    try {
      const { data } = await api.post("/remove-background", { url: fileUrl(art.image_url) });
      setPlacementArt((prev) => ({ ...prev, [p]: { ...prev[p], image_url: data.url } }));
      toast.success("Background removed", { id: tid });
    } catch { toast.error("Could not remove background", { id: tid }); }
    finally { setBgBusyPl(null); }
  };

  const { data: inv } = useQuery({
    queryKey: ["inv", product?.product_id, color, size],
    queryFn: async () => (await api.get(`/inventory/${product.product_id}`, { params: { color, size } })).data,
    enabled: !!(product && color && size),
  });
  const stock = inv?.stock ?? null;
  const filteredArt = useMemo(() => { const q = artSearch.trim().toLowerCase(); return q ? allDesigns.filter((d) => (d.title || "").toLowerCase().includes(q)) : allDesigns; }, [allDesigns, artSearch]);
  const feeMap = useMemo(() => Object.fromEntries(placementOpts.map((p) => [p.name, p.fee])), [placementOpts]);
  const placementExtra = useMemo(() => placements.slice(1).reduce((s, p) => s + (feeMap[p] || 0), 0), [placements, feeMap]);
  const normSize = (s) => (s || "").trim().toUpperCase().replace("XL", "X");
  const sizeFeeMap = useMemo(() => Object.fromEntries(sizePricing.map((t) => [normSize(t.size), t.upcharge])), [sizePricing]);
  const sizeUp = useCallback((s) => sizeFeeMap[normSize(s)] || 0, [sizeFeeMap]);
  const unitPrice = useMemo(() => (product && design ? product.base_price + design.base_price + placementExtra + sizeUp(size) : 0), [product, design, placementExtra, size, sizeUp]);
  const out = stock === 0;

  const changeProduct = (p) => { const vis = (p.colors || []).filter((c) => !(p.hidden_colors || []).includes(c)); setProduct(p); if (!p.sizes.includes(size)) setSize(p.sizes[0]); if (!vis.includes(color)) setColor(vis[0] || p.colors[0]); };
  const togglePlacement = (name) => setPlacements((prev) => {
    const idx = prev.indexOf(name);
    if (idx >= 0) { if (prev.length === 1) return prev; return prev.filter((x) => x !== name); }
    return [...prev, name];
  });

  const add = () => {
    addItem({
      kind: "apparel", name: `${design.title} on ${product.name}${product.style ? ` · ${product.style}` : ""}`, quantity: qty, unit_price: unitPrice,
      design_id: design.design_id, design_title: design.title, image_url: design.image_url,
      product_id: product.product_id, color, size, placement: placements.join(", "), placements,
      placement_art: placementArt, notes: notes.trim() || null,
      mockup: mockup ? { image_url: mockup.image_url, zones: mockup.zones, name: mockup.name, back_image_url: mockup.back_image_url || null, back_zones: mockup.back_zones || [], tintable: !!mockup.tintable, tint_hex: mockup.tintable ? ((product.color_hex || {})[color] || colorSwatch(color)) : null } : null,
    });
    toast.success(vendor ? "Added to your order" : "Added to cart");
    if (vendor) navigate("/vendor/checkout");
  };

  if (isLoading) return <p className="max-w-7xl mx-auto px-6 py-24 text-zinc-500 font-mono-p" data-testid="design-loading">Loading design…</p>;
  if (isError || !design) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="design-not-found">
        <h1 className="font-display text-2xl font-bold text-white">Design not found</h1>
        <Link to={home}><Button className="mt-6 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full">{vendor ? "Back to Vendor Market" : "Back to Catalog"}</Button></Link>
      </div>
    );
  }

  if (vendor && design && allDesigns.length > 0 && !allDesigns.some((d) => d.design_id === design.design_id)) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="design-not-in-market">
        <h1 className="font-display text-2xl font-bold text-white">That design isn't available at the market</h1>
        <Link to="/vendor"><Button className="mt-6 bg-[#ff6a13] hover:bg-[#ff8a3d] rounded-full">Back to Vendor Market</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="design-detail-page">
      <button onClick={() => navigate(home)} data-testid="back-to-catalog" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
        <ArrowLeft size={16} /> {vendor ? "Back to Vendor Market" : "Back to Catalog"}
      </button>

      <div className="grid md:grid-cols-2 gap-8 mt-6">
        <div className="md:sticky md:top-24 h-fit">
          <div className="checker rounded-2xl p-4 sm:p-8 flex items-center justify-center min-h-[200px] sm:min-h-[360px]" data-testid="design-preview-tile">
            <img src={thumbUrl(design.image_url, 800)} srcSet={`${thumbUrl(design.image_url, 480)} 480w, ${thumbUrl(design.image_url, 800)} 800w`} sizes="(max-width: 640px) 90vw, 600px" alt={design.title} className="max-w-full max-h-[240px] sm:max-h-[420px] object-contain drop-shadow-2xl" />
          </div>
          <div className="flex items-center justify-between gap-3 mt-2">
            <p className="text-[11px] font-mono-p text-zinc-500">Preview background · see it on a light or dark shirt</p>
            <BgToggle bg={bg} setBg={setBg} />
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <div className="flex flex-wrap gap-1.5 mb-2">{design.tags?.map((t) => <span key={t} className="text-[10px] font-mono-p uppercase tracking-wider text-[#22c3d6] border border-[#22c3d6]/30 rounded px-1.5 py-0.5">{t}</span>)}</div>
            <div className="flex items-start justify-between gap-3">
              {design.seq ? <p className="font-mono-p text-xs text-[#22c3d6] mb-1" data-testid="design-detail-seq">Design #{design.seq}</p> : null}
              <h1 className="font-display text-3xl font-extrabold text-white">{design.title}</h1>
              <FavoriteButton designId={design.design_id} size={20} className="shrink-0 mt-1" />
            </div>
            <p className="text-sm text-zinc-400 mt-1.5">{design.description}</p>
            <div className="mt-3">{!vendor && <ShareDesign design={design} />}</div>
          </div>

          {!product ? <p className="text-zinc-500 font-mono-p">Loading options…</p> : (
            <>
              <div>
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Apparel Type</p>
                <div className="grid grid-cols-2 gap-2">
                  {products.map((p) => (
                    <button key={p.product_id} onClick={() => changeProduct(p)} data-testid={`product-opt-${p.apparel_type}`}
                      className={`text-left p-2.5 rounded-xl border text-sm transition-colors ${product.product_id === p.product_id ? "border-[#ff6a13] bg-[#ff6a13]/10 text-white" : "border-zinc-800 bg-[#1e222d] text-zinc-300 hover:border-zinc-700"}`}>
                      <span className="block font-medium">{p.apparel_type}</span>
                      {(products.filter((x) => x.apparel_type === p.apparel_type).length > 1 || p.style) && <span className="block text-[11px] text-zinc-400 truncate" data-testid={`product-opt-style-${p.product_id}`}>{p.name}{p.style ? ` · ${p.style}` : ""}</span>}
                      <span className="text-xs text-zinc-500 font-mono-p">${(p.base_price + design.base_price).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Color{vendor && liveInv && <span className="text-[#10b981] normal-case tracking-normal"> · {liveInvRes?.mode === "pack" ? "showing what we brought today" : "showing colours in stock today"}</span>}</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.filter((c) => !(product.hidden_colors || []).includes(c) && colorHasStock(product, c)).map((c) => (
                    <button key={c} onClick={() => setColor(c)} title={c} data-testid={`color-opt-${c}`}
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110 ${color === c ? "border-[#ff6a13]" : "border-zinc-700"}`}
                      style={{ backgroundColor: (product.color_hex || {})[c] || colorSwatch(c) }}>
                      {color === c && <Check size={14} className={c === "White" || c === "Heather Grey" ? "text-black" : "text-white"} />}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Size <span className="text-zinc-600 normal-case tracking-normal">· larger sizes may cost more</span></p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => {
                    const n = vendor && liveInv ? stockOf(product.product_id, color, s) : null;
                    const gone = n !== null && n <= 0;
                    return (
                    <button key={s} onClick={() => !gone && setSize(s)} disabled={gone} data-testid={`size-opt-${s}`} title={gone ? "Not available today" : undefined}
                      className={`min-w-[44px] h-11 px-2 rounded-lg border text-sm font-mono-p transition-colors flex flex-col items-center justify-center leading-none ${gone ? "border-zinc-900 bg-[#15171d] text-zinc-600 line-through cursor-not-allowed" : size === s ? "border-[#ff6a13] bg-[#ff6a13]/10 text-white" : "border-zinc-800 bg-[#1e222d] text-zinc-300 hover:border-zinc-700"}`}>
                      <span>{s}</span>
                      {!gone && sizeUp(s) > 0 && <span className="text-[9px] text-[#ffd600] mt-0.5">+${sizeUp(s).toFixed(0)}</span>}
                      {n !== null && !gone && <span className={`text-[9px] mt-0.5 ${n <= 3 ? "text-[#ffd600]" : "text-[#10b981]"}`} data-testid={`size-stock-${s}`}>{n} left</span>}
                    </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Placement <span className="text-zinc-600 normal-case tracking-normal">· first included, extras added</span></p>
                {(!mockup || mockup.back_image_url) && (
                  <div className="flex gap-1.5 mb-2" data-testid="mock-view-toggle">
                    {["both", "front", "back"].map((v) => (
                      <button key={v} onClick={() => setMockView(v)} data-testid={`mock-view-${v}`}
                        className={`px-3 h-7 rounded-full text-[11px] font-mono-p uppercase tracking-wider border transition-colors ${mockView === v ? "border-[#ff6a13] bg-[#ff6a13]/10 text-white" : "border-zinc-800 bg-[#1e222d] text-zinc-400 hover:border-zinc-700"}`}>{v}</button>
                    ))}
                  </div>
                )}
                {mockup ? <ImageMockup mockup={mockup} placements={placements} options={placementOpts} onToggle={togglePlacement} art={placementArt} fileUrl={fileUrl} view={mockView} tint={(product.color_hex || {})[color] || colorSwatch(color)} />
                  : <GarmentDiagram placements={placements} options={placementOpts} onToggle={togglePlacement} color={(product.color_hex || {})[color] || colorSwatch(color)} art={placementArt} fileUrl={fileUrl} view={mockView} />}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {placementOpts.map((pl) => {
                    const idx = placements.indexOf(pl.name);
                    const selected = idx >= 0;
                    const isFirst = idx === 0;
                    return (
                      <button key={pl.name} onClick={() => togglePlacement(pl.name)} data-testid={`placement-opt-${pl.name}`}
                        className={`flex flex-col items-start justify-center px-3 h-12 rounded-lg border text-left transition-colors ${selected ? "border-[#ff6a13] bg-[#ff6a13]/10 text-white" : "border-zinc-800 bg-[#1e222d] text-zinc-300 hover:border-zinc-700"}`}>
                        <span className="text-xs sm:text-sm font-medium leading-tight">{pl.name}</span>
                        <span className="text-[10px] font-mono-p text-zinc-400">{selected ? (isFirst ? "Included" : `+$${pl.fee.toFixed(2)}`) : (pl.fee > 0 ? `+$${pl.fee.toFixed(2)}` : "Included")}</span>
                      </button>
                    );
                  })}
                </div>
                {placementExtra > 0 && <p className="text-[11px] text-zinc-400 font-mono-p mt-2" data-testid="placement-extra">+${placementExtra.toFixed(2)} for {placements.length - 1} extra placement{placements.length - 1 === 1 ? "" : "s"}</p>}

                {placements.length > 0 && (
                  <div className="mt-4" data-testid="placement-art-list">
                    <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Art per placement <span className="text-zinc-600 normal-case tracking-normal">· assign a design to each spot</span></p>
                    <div className="space-y-2">
                      {placements.map((p) => {
                        const art = placementArt[p] || {};
                        return (
                          <div key={p} className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-[#1e222d] p-2" data-testid={`placement-art-${p}`}>
                            <div className="w-11 h-11 checker rounded overflow-hidden flex items-center justify-center shrink-0">
                              {art.image_url && <img src={thumbUrl(art.image_url, 240)} alt="" className="max-w-full max-h-full object-contain" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white leading-tight">{p}</p>
                              <p className="text-[11px] text-zinc-500 font-mono-p truncate">{art.title || "—"}</p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => setArtPickerFor(p)} data-testid={`change-art-${p}`} className="h-8 border-zinc-700 bg-transparent text-white hover:bg-zinc-800 shrink-0"><ImageIcon size={13} className="mr-1" />Change art</Button>
                            {art.image_url && <Button size="sm" variant="outline" onClick={() => removeArtBg(p)} disabled={bgBusyPl === p} data-testid={`removebg-art-${p}`} title="Remove background" className="h-8 border-[#22c3d6]/40 bg-[#22c3d6]/10 text-[#22c3d6] hover:bg-[#22c3d6]/20 shrink-0 px-2"><Eraser size={13} /></Button>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <StockBadge stock={stock} />

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center hover:bg-zinc-700" data-testid="config-qty-minus"><Minus size={14} /></button>
                  <span className="w-8 text-center font-mono-p" data-testid="config-qty">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center hover:bg-zinc-700" data-testid="config-qty-plus"><Plus size={14} /></button>
                </div>
                <span className="font-display text-2xl font-bold text-white font-mono-p">${(unitPrice * qty).toFixed(2)}</span>
              </div>

              <div>
                <p className="text-xs text-zinc-400 font-mono-p mb-1">Special instructions (optional)</p>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="apparel-notes" rows={2} placeholder="e.g. print placement notes, colour match, deadline…" className="w-full rounded-lg bg-[#1e222d] border border-zinc-700 text-white text-sm p-2.5 font-mono-p focus:border-[#ff6a13] outline-none resize-y" />
              </div>

              <Button onClick={add} disabled={out} data-testid="add-to-cart-button" className="w-full bg-[#ff6a13] hover:bg-[#ff8a3d] disabled:opacity-40 text-white rounded-full h-12 font-semibold active:scale-95 transition-transform">
                {out ? "Out of Stock" : vendor ? "Add to my order" : "Add to Cart"}
              </Button>
            </>
          )}
        </div>
      </div>

      <Dialog open={!!artPickerFor} onOpenChange={(v) => { if (!v) { setArtPickerFor(null); setArtSearch(""); } }}>
        <DialogContent className="bg-[#12141a] border-zinc-800 text-zinc-100 max-w-lg" data-testid="art-picker-dialog">
          <DialogHeader><DialogTitle className="font-display">Choose art for {artPickerFor}</DialogTitle><DialogDescription className="text-zinc-500">Pick a catalog design or upload your own artwork for this placement.</DialogDescription></DialogHeader>
          <Input value={artSearch} onChange={(e) => setArtSearch(e.target.value)} placeholder="Search designs…" data-testid="art-picker-search" className="bg-[#1e222d] border-zinc-700 text-white" />
          <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-700 rounded-lg py-2.5 cursor-pointer hover:border-[#ff6a13] text-sm text-zinc-300 mt-1" data-testid="art-upload-label">
            <Upload size={14} /> {uploadingArt ? "Uploading…" : "Upload your own artwork"}
            <input type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={uploadArt} data-testid="art-upload-input" />
          </label>
          <p className="text-[10px] text-zinc-600 text-center">or choose from the catalog below · <GuidelinesLink testid="detail-guidelines-link" className="text-[10px]" /></p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto mt-1">
            {filteredArt.map((d) => {
              const active = placementArt[artPickerFor]?.design_id === d.design_id;
              return (
                <button key={d.design_id} onClick={() => assignArt(d)} data-testid={`art-option-${d.design_id}`} className={`rounded-lg border p-1.5 text-left transition-colors ${active ? "border-[#ff6a13] bg-[#ff6a13]/10" : "border-zinc-800 bg-[#1e222d] hover:border-zinc-600"}`}>
                  <div className="checker aspect-square rounded flex items-center justify-center overflow-hidden"><img src={thumbUrl(d.image_url, 240)} alt="" loading="lazy" className="max-w-full max-h-full object-contain" /></div>
                  <p className="text-[11px] text-zinc-300 truncate mt-1">{d.title}</p>
                </button>
              );
            })}
            {allDesigns.length === 0 && <p className="text-zinc-500 text-sm col-span-full p-2">No designs available.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
